import { useState, useEffect } from 'react'
import { useRoute } from 'wouter'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '../components/ui/badge'
import { SEOHead } from '@/components/seo-head'
import { buildApiUrl } from '@/config/api'
import {
  Building2,
  MapPin,
  Globe,
  Linkedin,
  Star,
  Eye,
  Users,
  Award,
  Calendar,
  Sparkles,
  ExternalLink,
  ArrowLeft,
  MessageCircle
} from 'lucide-react'

interface VendorProfile {
  id: string
  companyName: string
  tagline?: string
  description: string
  industry: string
  services: string[]
  location?: string
  website?: string
  linkedinUrl?: string
  foundedYear?: string
  companySize?: string
  certifications: string[]
  languages: string[]
  keywords: string[]
  logo?: string
  coverImage?: string
  gallery: string[]
  isVerified: boolean
  isFeatured: boolean
  views: number
  likes: number
  contacts: number
  slug: string
  createdAt: string
  user: {
    id: string
    name: string
  }
  testimonials: Array<{
    id: string
    clientName: string
    clientCompany: string
    clientPosition?: string
    content: string
    rating: number
    isVerified: boolean
    createdAt: string
  }>
  portfolioItems: Array<{
    id: string
    title: string
    description: string
    category: string
    technologies: string[]
    images: string[]
    liveUrl?: string
    githubUrl?: string
    duration?: string
    budget?: number
    createdAt: string
  }>
}

export default function VendorSpotlightProfilePage() {
  const [, params] = useRoute('/vendor-spotlight/:slug')
  const [profile, setProfile] = useState<VendorProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (params?.slug) {
      loadProfile(params.slug)
    }
  }, [params?.slug])

  const loadProfile = async (slug: string) => {
    try {
      setLoading(true)
      setError(null)
      const response = await fetch(buildApiUrl(`/api/vendor-profiles/${slug}/public`))
      const data = await response.json()

      if (data.success) {
        setProfile(data.data)
      } else {
        setError(data.message || 'Profile not found')
      }
    } catch (error) {
      console.error('Error loading profile:', error)
      setError('Failed to load profile')
    } finally {
      setLoading(false)
    }
  }

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${
          i < rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
        }`}
      />
    ))
  }

  const getAverageRating = (testimonials: VendorProfile['testimonials']) => {
    if (testimonials.length === 0) return 0
    const sum = testimonials.reduce((acc, t) => acc + t.rating, 0)
    return Math.round((sum / testimonials.length) * 10) / 10
  }

  // const formatDate = (dateString: string) => {
  //   return new Date(dateString).toLocaleDateString('en-US', {
  //     year: 'numeric',
  //     month: 'long',
  //     day: 'numeric'
  //   })
  // }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading vendor profile...</p>
        </div>
      </div>
    )
  }

  if (error || !profile) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Building2 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Profile Not Found</h2>
          <p className="text-gray-600 mb-4">{error || 'The vendor profile you are looking for does not exist.'}</p>
          <Button onClick={() => window.history.back()}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Go Back
          </Button>
        </div>
      </div>
    )
  }

  // Generate SEO metadata
  const seoTitle = profile ? `${profile.companyName} - Vendor Profile | SourcedIn` : 'Vendor Profile | SourcedIn'
  const seoDescription = profile 
    ? `${profile.companyName} - ${profile.tagline || profile.industry} vendor. ${profile.description.substring(0, 150)}...`
    : 'Professional vendor profile on SourcedIn marketplace'
  const seoKeywords = profile 
    ? `${profile.companyName}, ${profile.industry}, ${profile.services.join(', ')}, ${profile.keywords.join(', ')}, vendor, supplier`
    : 'vendor, supplier, business, marketplace'

  const structuredData = profile ? {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": profile.companyName,
    "description": profile.description,
    "url": profile.website,
    "industry": profile.industry,
    "address": profile.location ? {
      "@type": "PostalAddress",
      "addressLocality": profile.location
    } : undefined,
    "foundingDate": profile.foundedYear,
    "numberOfEmployees": profile.companySize,
    "aggregateRating": profile.testimonials.length > 0 ? {
      "@type": "AggregateRating",
      "ratingValue": getAverageRating(profile.testimonials),
      "reviewCount": profile.testimonials.length
    } : undefined,
    "review": profile.testimonials.map(testimonial => ({
      "@type": "Review",
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": testimonial.rating
      },
      "author": {
        "@type": "Person",
        "name": testimonial.clientName
      },
      "reviewBody": testimonial.content
    }))
  } : undefined

  return (
    <>
      <SEOHead
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        canonical={`https://sourcedin.ai/vendor-spotlight/${profile?.slug || params?.slug}`}
        structuredData={structuredData}
      />
      
      <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button
            variant="ghost"
            onClick={() => window.history.back()}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </div>
      </div>

      {/* Cover Image */}
      {profile.coverImage && (
        <div className="h-64 bg-gradient-to-r from-primary to-green-600">
          <img
            src={profile.coverImage}
            alt={`${profile.companyName} cover`}
            className="w-full h-full object-cover"
          />
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Company Header */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-center space-x-4">
                    {profile.logo ? (
                      <img
                        src={profile.logo}
                        alt={profile.companyName}
                        className="w-16 h-16 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="w-16 h-16 bg-primary rounded-lg flex items-center justify-center">
                        <Building2 className="w-8 h-8 text-white" />
                      </div>
                    )}
                    <div>
                      <h1 className="text-3xl font-bold text-gray-900">{profile.companyName}</h1>
                      {profile.tagline && (
                        <p className="text-lg text-gray-600 mt-1">{profile.tagline}</p>
                      )}
                      <div className="flex items-center space-x-4 mt-2">
                        {profile.location && (
                          <div className="flex items-center text-gray-500">
                            <MapPin className="w-4 h-4 mr-1" />
                            {profile.location}
                          </div>
                        )}
                        <div className="flex items-center text-gray-500">
                          <Eye className="w-4 h-4 mr-1" />
                          {profile.views} views
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col items-end space-y-2">
                    {profile.isVerified && (
                      <Badge className="bg-green-100 text-green-800">
                        <Award className="w-3 h-3 mr-1" />
                        Verified
                      </Badge>
                    )}
                    {profile.isFeatured && (
                      <Badge className="bg-yellow-100 text-yellow-800">
                        <Sparkles className="w-3 h-3 mr-1" />
                        Featured
                      </Badge>
                    )}
                  </div>
                </div>

                <div className="flex items-center space-x-6">
                  {profile.website && (
                    <a
                      href={profile.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center text-primary hover:text-primary/80"
                    >
                      <Globe className="w-4 h-4 mr-2" />
                      Website
                      <ExternalLink className="w-3 h-3 ml-1" />
                    </a>
                  )}
                  {profile.linkedinUrl && (
                    <a
                      href={profile.linkedinUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center text-blue-600 hover:text-blue-800"
                    >
                      <Linkedin className="w-4 h-4 mr-2" />
                      LinkedIn
                      <ExternalLink className="w-3 h-3 ml-1" />
                    </a>
                  )}
                  <Button className="bg-primary hover:bg-primary/90">
                    <MessageCircle className="w-4 h-4 mr-2" />
                    Contact Vendor
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Description */}
            <Card>
              <CardHeader>
                <CardTitle>About {profile.companyName}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                  {profile.description}
                </p>
              </CardContent>
            </Card>

            {/* Services */}
            <Card>
              <CardHeader>
                <CardTitle>Services Offered</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {profile.services.map((service, index) => (
                    <Badge key={index} variant="outline" className="text-sm py-1">
                      {service}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Portfolio */}
            {profile.portfolioItems.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Portfolio</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-6">
                    {profile.portfolioItems.map((item) => (
                      <div key={item.id} className="border rounded-lg p-4">
                        <h4 className="font-semibold text-gray-900 mb-2">{item.title}</h4>
                        <p className="text-gray-600 text-sm mb-3">{item.description}</p>
                        
                        {item.technologies.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-3">
                            {item.technologies.map((tech, index) => (
                              <Badge key={index} variant="secondary" className="text-xs">
                                {tech}
                              </Badge>
                            ))}
                          </div>
                        )}

                        <div className="flex items-center justify-between text-sm text-gray-500">
                          <span>{item.category}</span>
                          <div className="flex space-x-4">
                            {item.liveUrl && (
                              <a
                                href={item.liveUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary hover:text-primary/80"
                              >
                                View Live
                              </a>
                            )}
                            {item.githubUrl && (
                              <a
                                href={item.githubUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-gray-600 hover:text-gray-800"
                              >
                                GitHub
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Testimonials */}
            {profile.testimonials.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Client Testimonials</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    {profile.testimonials.map((testimonial) => (
                      <div key={testimonial.id} className="border-l-4 border-primary pl-4">
                        <div className="flex items-center mb-2">
                          <div className="flex items-center mr-3">
                            {renderStars(testimonial.rating)}
                          </div>
                          {testimonial.isVerified && (
                            <Badge variant="secondary" className="text-xs">
                              <Award className="w-3 h-3 mr-1" />
                              Verified
                            </Badge>
                          )}
                        </div>
                        <p className="text-gray-700 mb-3">"{testimonial.content}"</p>
                        <div className="text-sm text-gray-600">
                          <span className="font-medium">{testimonial.clientName}</span>
                          {testimonial.clientPosition && (
                            <span>, {testimonial.clientPosition}</span>
                          )}
                          <span> at {testimonial.clientCompany}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Company Info */}
            <Card>
              <CardHeader>
                <CardTitle>Company Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center">
                  <Building2 className="w-4 h-4 mr-3 text-gray-400" />
                  <span className="text-sm text-gray-600">Industry:</span>
                  <span className="ml-2 font-medium">{profile.industry}</span>
                </div>
                
                {profile.foundedYear && (
                  <div className="flex items-center">
                    <Calendar className="w-4 h-4 mr-3 text-gray-400" />
                    <span className="text-sm text-gray-600">Founded:</span>
                    <span className="ml-2 font-medium">{profile.foundedYear}</span>
                  </div>
                )}
                
                {profile.companySize && (
                  <div className="flex items-center">
                    <Users className="w-4 h-4 mr-3 text-gray-400" />
                    <span className="text-sm text-gray-600">Size:</span>
                    <span className="ml-2 font-medium">{profile.companySize}</span>
                  </div>
                )}

                {profile.testimonials.length > 0 && (
                  <div className="pt-4 border-t">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Rating:</span>
                      <div className="flex items-center">
                        <div className="flex items-center mr-2">
                          {renderStars(getAverageRating(profile.testimonials))}
                        </div>
                        <span className="text-sm font-medium">
                          {getAverageRating(profile.testimonials)} ({profile.testimonials.length} reviews)
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Certifications */}
            {profile.certifications.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Certifications</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {profile.certifications.map((cert, index) => (
                      <div key={index} className="flex items-center">
                        <Award className="w-4 h-4 mr-2 text-yellow-500" />
                        <span className="text-sm">{cert}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Languages */}
            {profile.languages.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Languages</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {profile.languages.map((language, index) => (
                      <Badge key={index} variant="outline" className="text-sm">
                        {language}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Contact CTA */}
            <Card className="bg-primary text-white">
              <CardContent className="pt-6 text-center">
                <Building2 className="w-12 h-12 mx-auto mb-4 opacity-80" />
                <h3 className="text-lg font-semibold mb-2">Interested in working together?</h3>
                <p className="text-sm opacity-90 mb-4">
                  Get in touch with {profile.companyName} to discuss your project needs.
                </p>
                <Button variant="secondary" className="w-full">
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Contact {profile.companyName}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
    </>
  )
}
